<template>
  <Loader v-if="loader" :loader="loader" />
  <section
    v-else
    class="grid grid-cols-12 gap-5 py-10 px-5 lg:px-2 cursor-default"
  >
    <form
      id="form-theme"
      @submit.prevent="submit"
      class="col-span-12 xl:col-span-4"
    >
      <header class="flex items-center gap-1">
        <Button
          icon="pi pi-arrow-left"
          class="btn-simple"
          @click.prevent="$router.go(-1)"
        />
        <h3>{{ $t("theme.org.title") }}</h3>
      </header>

      <div class="my-10">
        <label for="subdomain">{{ $t("theme.org.form.subdomain") }}</label>
        <InputText
          id="subdomain"
          v-model="form.subdomain"
          class="!mt-2"
          :disabled="disableDns"
        />
        <Errors :errors="v$.subdomain.$errors" />
      </div>

      <div class="grid grid-cols- gap-5 mb-10">
        <div id="logo-picker">
          <ImagePicker
            input-id="images"
            :label="$t('theme.org.form.logo')"
            v-model="form.logo"
            :max-files="1"
            accept="image/svg+xml"
            @update:removedFiles="onRemoveFiles"
          />
          <Errors :errors="v$.logo.$errors" />
        </div>

        <div id="partners-picker">
          <ImagePicker
            input-id="images/partners"
            :label="$t('theme.org.form.partners')"
            v-model="form.partners"
            :max-files="5"
            accept="image/svg+xml"
            @update:removedFiles="onRemoveFiles"
          />
          <Errors :errors="v$.partners.$errors" />
        </div>
      </div>

      <div id="colors" class="grid grid-cols-1 gap-8 mb-10">
        <ColorPicker
          v-model="colors.primary"
          :label="$t('theme.org.primary')"
        />
        <ColorPicker
          v-model="colors.secondary"
          :label="$t('theme.org.secondary')"
        />
      </div>

      <div id="actions" class="flex justify-end items-center gap-2">
        <Button
          :label="$t('labels.cancel')"
          class="btn-outline"
          @click.prevent="goBack()"
        />
        <Button type="submit" :label="$t('labels.save')" class="btn-primary" />
      </div>
    </form>

    <div
      id="preview"
      class="col-span-12 xl:col-span-8 border-l-2 pl-5 hidden xl:block"
    >
      <h3 class="mb-8">{{ $t("theme.org.form.colors") }}</h3>
      <p class="font-bold text-lg mb-2">{{ $t("theme.org.primary") }}</p>
      <PreviewColors :color="colors?.primary" />

      <p class="font-bold text-lg mb-2">{{ $t("theme.org.secondary") }}</p>
      <PreviewColors :color="colors?.secondary" />
    </div>
  </section>
</template>

<script lang="ts">
import { ThemePayload } from "@/models/theme.model";
import { ThemeRest } from "@/services/rest/themes.rest";
import { generateShades, useThemeStore } from "@/store/theme";
import useVuelidate from "@vuelidate/core";
import { computed, defineComponent, onMounted, ref } from "vue";

import { useRoute, useRouter } from "vue-router";
import { EditUserService } from "../edit-user.service";

import { Roles } from "@/models/user.model";
import { getUserFromStorage } from "@/utils/user-store.util";
import ColorPicker from "./color-picker.vue";
import ImagePicker from "./image-picker.vue";
import PreviewColors from "./preview-colors.vue";

export default defineComponent({
  name: "ThemeForm",
  components: { ColorPicker, ImagePicker, PreviewColors },
  setup() {
    const storeTheme = useThemeStore;
    const rules = new EditUserService().themeRules;
    const form = ref({} as ThemePayload);
    const colors = ref({ primary: "#153c59", secondary: "#d2b604" });
    const loader = ref(true);
    const v$ = useVuelidate<ThemePayload>(rules, form);
    const router = useRouter();
    const route = useRoute();
    const removedFiles = ref<{ inputId: string; image: File }[]>([]);
    const subdomain = window.location.hostname.split(".")[0];
    const id = route.params.id ? true : false;

    const disableDns = computed(() => {
      const role = getUserFromStorage().role;
      if (role === Roles.APPLICATION_ADMIN)
        return Boolean(storeTheme.filesById?.theme);
      else return Boolean(storeTheme.files?.theme);
    });

    function submit(): void {
      v$.value.$touch();
      if (v$.value.$invalid) return;
      const formData = prepareFormData();
      loader.value = true;
      isThemeExisting() ? update(formData) : create(formData);
    }

    function prepareFormData(): FormData {
      const formData = new FormData();
      const themeData = createThemeData();

      appendFilesToFormData(formData, "logo", form.value.logo);
      appendFilesToFormData(formData, "partners", form.value.partners);

      formData.append(
        "theme",
        new Blob([JSON.stringify(themeData)], { type: "application/json" })
      );

      if (!isThemeExisting()) {
        formData.append("subdomain", form.value.subdomain.toLowerCase());
        formData.append("organizationId", String(route.params.id));
      }

      appendRemovedFiles(formData);
      return formData;
    }

    function isThemeExisting(): boolean {
      return !!storeTheme.filesById?.theme || !!storeTheme.files?.subdomain;
    }

    function createThemeData() {
      return {
        colors: {
          "h-primary": String(colors.value.primary),
          "h-secondary": String(colors.value.secondary),
        },
      };
    }

    function goBack(): void {
      if (getUserFromStorage().role === Roles.APPLICATION_ADMIN) {
        router.push({ name: "organizations" });
        return;
      }
      router.go(-1);
    }

    function appendFilesToFormData(
      formData: FormData,
      key: string,
      files: any[]
    ): void {
      files.forEach((file) => {
        if (!file.image) formData.append(key, file);
      });
    }

    function appendRemovedFiles(formData: FormData): void {
      if (removedFiles.value.length === 0) return;
      const filesToRemove = removedFiles.value.map(
        (file) => `${file.inputId}/` + (file.image as any)?.image?.name
      );

      formData.append("filesRemove", JSON.stringify(filesToRemove));
    }

    function onRemoveFiles($event): void {
      removedFiles.value = $event;
      v$.value.$reset();
    }

    function create(formData): void {
      if (getUserFromStorage().role !== Roles.APPLICATION_ADMIN) return;
      ThemeRest.addTheme(formData)
        .then(() => router.push({ name: "organizations" }))
        .catch(() => v$.value.$reset())
        .finally(() => (loader.value = false));
    }

    function update(formData): void {
      ThemeRest.updateTheme(storeTheme.themeId, formData)
        .then(() => router.go(-1))
        .catch(() => v$.value.$reset())
        .finally(() => (loader.value = false));
    }

    function handleInvalidRoute() {
      loader.value = false;
      router.go(-1);
    }

    function loadAdminThemeData(): void {
      storeTheme
        .getFilesById(String(route.params.id))
        .then(() => populateFormWithThemeData(storeTheme.filesById))
        .finally(() => (loader.value = false));
    }

    function loadUserThemeData(): void {
      storeTheme
        .getFiles()
        .then(() => {
          if (storeTheme.files.subdomain !== subdomain) {
            router.go(-1);
            return;
          }
          populateFormWithThemeData(storeTheme.files);
        })
        .finally(() => (loader.value = false));
    }

    function populateFormWithThemeData(themeData): void {
      form.value.subdomain = String(themeData.subdomain ?? "");
      form.value.logo = mapImagesToForm(themeData.images, "LOGO");
      form.value.partners = mapImagesToForm(themeData.images, "PARTNERS");

      colors.value = {
        primary: String(themeData?.theme?.colors["h-primary"] ?? "#153c59"),
        secondary: String(themeData?.theme?.colors["h-secondary"] ?? "#d2b604"),
      };
    }

    function mapImagesToForm(images, type) {
      return images
        .filter((image) => image.type === type)
        .map((image) => ({ image: image.image }));
    }

    onMounted(() => {
      if (!route.params.id) {
        handleInvalidRoute();
        return;
      }

      const userRole = getUserFromStorage().role;
      if (userRole === Roles.APPLICATION_ADMIN) loadAdminThemeData();
      else loadUserThemeData();
    });

    return {
      storeTheme,
      form,
      v$,
      id,
      colors,
      loader,
      subdomain,
      removedFiles,
      disableDns,
      submit,
      generateShades,
      onRemoveFiles,
      goBack,
    };
  },
});
</script>
